import { CheckCircleRounded, CloseRounded } from '@mui/icons-material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { IAlert } from 'app/shared/model/alert.model';
import { Loading } from 'app/shared/util/loading';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Translate } from 'react-jhipster';
import { getEntitiesDefault as getAlerts } from '../../entities/alert/alert.reducer';

interface IAlertDefaultSelectionModalProps {
  handleSave: (data: { selectedAlerts: number[] }) => void;
  toggleModal: () => void;
  initialSelectedAlerts: number[];
  open: boolean;
  alerts?: IAlert[];
  companyId?: number | string;
  title?: string;
}

export const AlertDefaultSelectionModal = ({
  handleSave,
  toggleModal,
  initialSelectedAlerts,
  open,
  alerts: alertsProp,
  companyId,
  title,
}: IAlertDefaultSelectionModalProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const reduxAlertList = useAppSelector(state => state.alert.entities as IAlert[]);
  const reduxLoading = useAppSelector(state => state.alert.loading);

  const alertList = alertsProp ?? reduxAlertList;
  const loading = alertsProp ? false : reduxLoading;

  const [selectedAlerts, setSelectedAlerts] = useState<Set<number>>(initialSelectedAlerts ? new Set(initialSelectedAlerts) : new Set<number>());
  const [debouncedClusterFilterValue, setDebouncedClusterFilterValue] = useState('');
  const [debouncedAlertFilterValue, setDebouncedAlertFilterValue] = useState('');
  const [clusterFilterValue, setClusterFilterValue] = useState('');
  const [alertFilterValue, setAlertFilterValue] = useState('');
  const [filtering, setFiltering] = useState(false);

  const debouncedSetClusterFilterValue = useCallback(
    debounce(value => {
      setDebouncedClusterFilterValue(value);
      setFiltering(false);
    }, 1000),
    []
  );

  const debouncedSetAlertFilterValue = useCallback(
    debounce(value => {
      setDebouncedAlertFilterValue(value);
      setFiltering(false);
    }, 1000),
    []
  );

  const handleClusterInputChange = event => {
    const { value } = event.target;

    if (value) {
      setClusterFilterValue(value);
      setFiltering(true);
      debouncedSetClusterFilterValue(value);
    } else {
      setClusterFilterValue('');
      setDebouncedClusterFilterValue('');
    }
  };

  const handleAlertInputChange = event => {
    const { value } = event.target;

    if (value) {
      setAlertFilterValue(value);
      setFiltering(true);
      debouncedSetAlertFilterValue(value);
    } else {
      setAlertFilterValue('');
      setDebouncedAlertFilterValue('');
    }
  };

  useEffect(() => {
    if (!alertsProp && companyId !== undefined) {
      dispatch(
        getAlerts({
          page: 0,
          size: 10000,
          sort: 'name',
          companyId,
        })
      );
    }
  }, []);

  const groupedAlerts = useMemo(() => {
    const groupedByCluster = alertList
      .filter(alert => alert.id != null)
      .reduce((acc, alert) => {
        const clusterName = alert.alertCluster || 'Sem Cluster';
        if (!acc[clusterName]) {
          acc[clusterName] = [];
        }
        acc[clusterName].push(alert);
        return acc;
      }, {} as Record<string, IAlert[]>);

    const sortedClusterNames = Object.keys(groupedByCluster).sort((a, b) => {
      const hasSelectedA = groupedByCluster[a].some(alert => initialSelectedAlerts?.includes(alert.id));
      const hasSelectedB = groupedByCluster[b].some(alert => initialSelectedAlerts?.includes(alert.id));
      if (hasSelectedA && !hasSelectedB) return -1;
      if (!hasSelectedA && hasSelectedB) return 1;
      return a.localeCompare(b);
    });

    return sortedClusterNames.map(clusterName => ({
      clusterName,
      clusterKey: clusterName === 'Sem Cluster' ? 'null' : clusterName,
      alerts: groupedByCluster[clusterName].sort((a, b) => {
        const isSelectedA = initialSelectedAlerts?.includes(a.id);
        const isSelectedB = initialSelectedAlerts?.includes(b.id);
        if (isSelectedA && !isSelectedB) return -1;
        if (!isSelectedA && isSelectedB) return 1;
        return (a.name || '').localeCompare(b.name || '');
      }),
    }));
  }, [alertList]);

  const groupedAndSortedAlerts = useMemo(() => {
    if (!debouncedClusterFilterValue && !debouncedAlertFilterValue) {
      return groupedAlerts;
    }
    return groupedAlerts
      .map(group => ({
        ...group,
        alerts: group.alerts.filter(alert =>
          (alert.name || '').toLowerCase().includes(debouncedAlertFilterValue.toLowerCase())
        ),
      }))
      .filter(group =>
        group.clusterName.toLowerCase().includes(debouncedClusterFilterValue.toLowerCase()) &&
        group.alerts.length > 0
      );
  }, [groupedAlerts, debouncedClusterFilterValue, debouncedAlertFilterValue]);

  const handleSelectAll = () => {
    const allAlertIds = alertList.filter(alert => alert.id != null).map(alert => alert.id);
    if (selectedAlerts.size === allAlertIds.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(allAlertIds));
    }
  };

  const handleSelectCluster = clusterName => {
    const clusterAlerts = groupedAndSortedAlerts.find(cluster => cluster.clusterName === clusterName)?.alerts;
    if (!clusterAlerts) return;

    const allSelected = clusterAlerts.every(alert => selectedAlerts.has(alert.id));
    if (allSelected) {
      setSelectedAlerts(prevSelected => {
        const newSelected = new Set(prevSelected);
        clusterAlerts.forEach(alert => newSelected.delete(alert.id));
        return newSelected;
      });
    } else {
      setSelectedAlerts(prevSelected => {
        const newSelected = new Set(prevSelected);
        clusterAlerts.forEach(alert => newSelected.add(alert.id));
        return newSelected;
      });
    }
  };

  const handleSelectAlert = alertId => {
    setSelectedAlerts(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(alertId)) {
        newSelected.delete(alertId);
      } else {
        newSelected.add(alertId);
      }
      return newSelected;
    });
  };

  const handleSaveClick = () => {
    const data = {
      selectedAlerts: Array.from(selectedAlerts),
    };
    handleSave(data);
    toggleModal();
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={toggleModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '15px', background: theme.palette.primary.main } }}
        sx={{
          boxShadow:
            'rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px',
        }}
      >
        <DialogTitle id="dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title ? (
            <Typography variant="h6" sx={{ fontWeight: 500, display: 'flex', alignItems: 'baseline', gap: 1 }}>
              {title}
              <Typography component="span" variant="caption" sx={{ opacity: 0.7, fontWeight: 400 }}>
                ({selectedAlerts.size}/{alertList.filter(a => a.id != null).length} selecionados)
              </Typography>
            </Typography>
          ) : (
            <Typography variant="h6">Selecione os clusters e alertas</Typography>
          )}
          <IconButton onClick={toggleModal}>
            <CloseRounded color="secondary" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {!loading && !!alertList?.length && (
            <>
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={2} gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={clusterFilterValue}
                  onChange={handleClusterInputChange}
                  placeholder="Buscar por cluster"
                  InputProps={{
                    sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                    endAdornment: (
                      <InputAdornment position="end">
                        {!clusterFilterValue ? (
                          <SearchOutlinedIcon color="action" />
                        ) : (
                          <IconButton size="small" onClick={() => handleClusterInputChange({ target: { value: '' } })}>
                            <CloseOutlinedIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  value={alertFilterValue}
                  onChange={handleAlertInputChange}
                  placeholder="Buscar por alerta"
                  InputProps={{
                    sx: { borderRadius: '8px', p: '0 8px 2px 0' },
                    endAdornment: (
                      <InputAdornment position="end">
                        {!alertFilterValue ? (
                          <SearchOutlinedIcon color="action" />
                        ) : (
                          <IconButton size="small" onClick={() => handleAlertInputChange({ target: { value: '' } })}>
                            <CloseOutlinedIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedAlerts.size === alertList.filter(alert => alert.id != null).length}
                      onChange={handleSelectAll}
                      color="secondary"
                    />
                  }
                  label="Selecionar Todos"
                />
              </Box>
            </>
          )}
          <Box sx={{ mt: 1 }}>
            {filtering || loading ? (
              <Loading />
            ) : (
              groupedAndSortedAlerts.map(({ clusterName, clusterKey, alerts }) => (
                <Accordion
                  key={clusterName}
                  sx={{
                    my: 1,
                    borderRadius: '8px',
                    '&:before': {
                      display: 'none',
                    },
                  }}
                  defaultExpanded={alerts.some(alert => selectedAlerts.has(alert.id))}
                >
                  <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={e => [e.stopPropagation(), handleSelectCluster(clusterName)]}
                      style={{ cursor: 'pointer' }}
                      mb={1}
                    >
                      <Checkbox
                        checked={alerts.every(alert => selectedAlerts.has(alert.id))}
                        indeterminate={alerts.some(alert => selectedAlerts.has(alert.id)) && !alerts.every(alert => selectedAlerts.has(alert.id))}
                        color="secondary"
                      />
                      <Typography color="primary.contrastText">
                        {' '}
                        <Translate contentKey={`fleetSenseApp.AlertCluster.${clusterKey}`}>{clusterName}</Translate>
                      </Typography>
                    </Box>
                    <CheckCircleRounded
                      color="success"
                      fontSize="small"
                      sx={{
                        ml: 1,
                        visibility: alerts.some(alert => selectedAlerts.has(alert.id)) ? 'visible' : 'hidden',
                      }}
                    />
                  </AccordionSummary>

                  <AccordionDetails>
                    {alerts.map(alert => (
                      <Box key={alert.id} display="flex" alignItems="center" onClick={() => handleSelectAlert(alert.id)} style={{ cursor: 'pointer' }} mb={1}>
                        <Checkbox checked={selectedAlerts.has(alert.id)} color="secondary" />
                        <Box ml={1}>
                          <Typography variant="subtitle2">{alert.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Tipo: <Translate contentKey={`fleetSenseApp.AlertType.${alert.alertType}`}>{alert.alertType}</Translate>
                            {alert.sensor?.name && ` | Sensor: ${alert.sensor.name}`}
                            {alert.value && ` | Valor: ${alert.value}`}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
          {!loading && !alertList?.length && (
            <Box display="flex" justifyContent="center" mt={2} mb={2}>
              <Typography variant="body2" color="text.secondary">
                Nenhum alerta padrão disponivel.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '8px 24px', my: 2 }}>
          <Button onClick={toggleModal} disableRipple variant="text" sx={{ color: 'text.primary' }}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClick} disableRipple variant="contained" color="secondary">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
